/**
 * @file BrandIcon.tsx
 * @brief Square icon mark — spartan helmet + play button in brand red.
 *
 * @details
 * Rendered straight from `/brand/logo.png` (copied from the mobile app's
 * `FRONTEND/src/assets/logo.png`). Works on any background because the
 * red mark sits on transparent pixels. Use anywhere we need a small,
 * recognisable mark — sidebar header, topbar avatar fallback, browser
 * favicon mirror, etc.
 *
 * @dependencies
 * - WEBPANEL/public/brand/logo.png (asset, served as a static file)
 *
 * @references
 * - FRONTEND/src/assets/logo.png (source-of-truth in mobile app)
 *
 * @author DES
 * @date 26-05-2026
 */

import type { HTMLAttributes } from "react";
import { cn } from "@/shared/theme/cn";

interface BrandIconProps extends HTMLAttributes<HTMLImageElement> {
  /** Pixel size for both width and height (default 24). */
  size?: number;
  /** Override the public asset path if a different mark is needed. */
  src?: string;
}

export function BrandIcon({
  size = 24,
  src = "/brand/logo.png",
  className,
  ...rest
}: BrandIconProps) {
  return (
    <img
      src={src}
      width={size}
      height={size}
      alt="Verblizr"
      draggable={false}
      className={cn("select-none object-contain", className)}
      {...rest}
    />
  );
}
